import {
	useResendVerificationEmailMutation as useResendVerificationEmailMutationGQL,
	useResendVerificationEmailToAllUnverifiedMutation,
} from "@repo/server-api";
import { useNotifier } from "@repo/shared/common";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type UseResendVerificationEmailReturn = {
	send: (userId: string) => Promise<void>;
	sendAll: () => Promise<void>;
};

export function useResendVerificationEmail(): UseResendVerificationEmailReturn {
	const { t } = useTranslation();
	const notifier = useNotifier();

	const [resendEmailMutation] = useResendVerificationEmailMutationGQL();
	const [resendAllEmailMutation] = useResendVerificationEmailToAllUnverifiedMutation();

	const send = useCallback(
		async (userId: string): Promise<void> => {
			const response = await resendEmailMutation({
				variables: {
					userid: userId,
					redirectUri: window.location.origin,
					clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
				},
			});

			if (
				(response.errors != null && response.errors.length > 0) ||
				response.data?.resendVerificationEmail.boolean !== true
			) {
				notifier.notifyFailure(t("error-resending-verification-email"));
				return;
			}

			notifier.notifySuccess(t("verification-email-resent-successfully"));
		},
		[resendEmailMutation, notifier, t],
	);

	const sendAll = useCallback(async (): Promise<void> => {
		const response = await resendAllEmailMutation({
			variables: {
				redirectUri: window.location.origin,
				clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
			},
		});

		if (
			(response.errors != null && response.errors.length > 0) ||
			response.data?.resendVerificationEmailToAllUnverified.boolean !== true
		) {
			notifier.notifyFailure(t("error-resending-verification-email"));
			return;
		}

		notifier.notifySuccess(t("verification-email-resent-successfully"));
	}, [resendAllEmailMutation, notifier, t]);

	return {
		send,
		sendAll,
	};
}
